<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div :class="bounceTransition" class="modal-body">
        <div class="modal-header">
          <h1>{{ title }}</h1>
          <div style="height: 30px; width: 30px" v-on:click="closeModal">
            <CloseSVG :propId="'login-signup-close'" />
          </div>
        </div>
        <div id="forgot-pass-wrap">
          <div id="return-to-login" @click="returnToLogin">
            <img id="arrow" src="@/assets/light-blue-arrow-left.svg" />
            <span>Back to log in</span>
          </div>
          <p class="form-group">
            Please enter your account email below. We'll send you an email containing your new
            password.
          </p>
          <div class="form-group">
            <label for="recover-email"> Account Email </label>
            <input
              type="email"
              id="recover-email"
              class="form-input"
              v-model="emailInput"
              required
              placeholder="Your email"
            />
          </div>
          <div class="form-group">
            <div id="error-message" v-if="showErrorMessage">
              {{ errorMessage }}
            </div>

            <div id="error-message" v-else-if="showInvalidEmailError">
              This email is not registered.
            </div>
            <!-- <div id="error-message" v-else-if="showInvalidEmailError">
              This email is not registered. Would you like to
              <a href="#" class="modal-link" @click="returnToLogin">
                Sign up
              </a>
              for a new account?
            </div> -->
          </div>
          <button
            v-if="!loading"
            aria-label="Reset password"
            class="modal-select-btn"
            @click="handleReset"
          >
            Reset Password
          </button>
          <div v-else id="loading-wrap">
            <img src="@/assets/loader.svg" />
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, onMounted, inject } from "vue";
import axios from "axios";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { ActionTypes } from "@/store/actions";

export default defineComponent({
  name: "ForgotPassword",
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const title = ref("");
    onMounted(() => {
      //Trigger rerender on mount
      title.value = "Forgot Password?";
    });

    const returnToLogin = () => {
      store.dispatch(ActionTypes.SET_MODAL, "loginModal");
    };

    const emailInput = ref("");
    const errorMessage = ref("");
    const showErrorMessage = ref(false);
    const showInvalidEmailError = ref(false);

    /* eslint-disable no-useless-escape */
    const validateEmail = (email: string) => {
      var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    };

    const loading = ref(false);
    const handleReset = async () => {
      loading.value = true;

      if (validateEmail(emailInput.value)) {
        try {
          let res = await axios.post(
            `${process.env.VUE_APP_API_URL}/editor/customers/ajax.php?mode=forgotPassword`,
            { email: emailInput.value }
          );

          if (typeof res.data.error !== "undefined") {
            if (res.data.message.includes("email does not have an account")) {
              showInvalidEmailError.value = true;
              showErrorMessage.value = false;
            } else {
              errorMessage.value = res.data.message;
              showErrorMessage.value = true;
            }
          } else {
            showErrorMessage.value = false;
            closeModal();
          }

          loading.value = false;
        } catch (e) {
          console.log(e);
          loading.value = false;
        }
      } else {
        errorMessage.value = "Please enter a valid email";
        showErrorMessage.value = true;
        loading.value = false;
      }
    };

    return {
      closeModal,
      fadeTransition,
      bounceTransition,
      title,
      returnToLogin,
      emailInput,
      errorMessage,
      showErrorMessage,
      showInvalidEmailError,
      loading,
      handleReset,
    };
  },
});
</script>

<style scoped>
.modal-body {
  padding: 4rem;
  width: 450px;
  height: 500px;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 2rem;
}

.modal-header h1 {
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.25px;
}

.close {
  border-radius: 50%;
  border: solid 1px black;
  background-color: white;
  height: 25px;
  width: 25px;
}

#forgot-pass-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

#return-to-login,
.form-group {
  padding-bottom: 2rem;
}


#forgot-pass-wrap p {
  font-size: 14px;
}

#return-to-login {
  font-size: 16px;
  font-weight: 700;
  color: #0dc5e1;
  display: flex;
  align-items: center;
}

#return-to-login:hover {
  cursor: pointer;
  text-decoration: underline;
}

#return-to-login img {
  height: 20px;
  margin-right: 5px;
}

#input-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}

#input-wrap p {
  font-size: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.form-group label,
.form-group div {
  margin-bottom: 5px;
  font-size: 16px;
  font-weight: 700;
}

.form-group div {
  color: #0dc5e1;
}

.form-input {
  border-radius: 4px;
  border: 1px solid #ccc;
  height: 25px;
  padding: 6px 12px;
}

.form-input:focus {
  border-color: #66afe9;
  outline: 0;
  -webkit-box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%);
}

#error-message {
  color: red;
  font-size: 12px;
}

#loading-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
}

.modal-select-btn {
  border: none;
  border-radius: 4px;
  background: #ff6c00;
  color: white;
  font-size: 16px;
  padding: 1.5rem;
  margin-top: 2rem;
  text-decoration: none;
  text-align: center;
  width: 100%;
  text-shadow: none;
}

.modal-select-btn:hover {
  background: linear-gradient(to bottom, #ff994e 0%, #ff6c00 100%);
}

.modal-select-btn:disabled {
  opacity: 0.5;
}

@media (max-width: 767px) {
  .modal-body {
    width: 450px;
    height: 450px;
    position: fixed;
  }
}

@media (max-width: 500px) {
  .modal-body {
    width: 95%;
  }
}
</style>
